import React, { useState } from "react";
import {
  Autocomplete,
  Paper,
  Box,
  FormHelperText,
  MenuItem,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material";
import { COLORS } from "../../assets/theme";
import CustomTextField from "./CustomTextField";
import CustomLabel from "./CustomLabel";
import CustomCheckbox from "../CustomCheckbox";
import type { FilterOptionsState } from "@mui/material";

type DropdownOption = { label: string; value: string | number };

export interface IAutoCompleteProps {
  options: DropdownOption[];
  label?: string;
  value:
    | string
    | DropdownOption
    | number
    | boolean
    | Array<string | DropdownOption>;
  onChange: (value: string | number | (string | number)[]) => void;

  multiple?: boolean;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  sx?: SxProps<Theme>;
  renderInput?: (params: any) => React.ReactNode;
  getOptionLabel?: (option: string | DropdownOption) => string;
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  filterOptions?: (
    options: (string | DropdownOption)[],
    state: FilterOptionsState<string | DropdownOption>
  ) => (string | DropdownOption)[];
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string | DropdownOption
  ) => React.ReactNode;
  freeSolo?: boolean;
  valueProp?: any;
  width?: string;
  minWidth?: string;
  highlightLabelStar?: boolean;
  selectAll?: boolean;
  /** When set, shows "Select First N" instead of Select All and caps selection at N. */
  selectFew?: number;
  helperText?: string;
  hideChips?: boolean;
  disablePortal?: boolean;
  labelComponent?: React.ReactNode;
  isDeleted?: boolean;
  onToggleDeleted?: () => void;
  getOptionDisabled?: (option: DropdownOption) => boolean;
  onOpen?: () => void;
}

function findOptionByValue(
  value: string | number,
  options: DropdownOption[]
): DropdownOption {
  const found = options.find((opt) => String(opt.value) === String(value));
  if (found) return found;
  if (value === null || value === undefined || value === "") {
    return { label: "", value: "" };
  }
  if (typeof value === "number") {
    return { label: "", value: "" };
  }
  return { label: value, value };
}

const SELECT_ALL_OPTION = { label: "__SELECT_ALL__", value: "__SELECT_ALL__" };

const CustomPaper = (
  props: any,
  isDeleted: boolean,
  onToggleDeleted?: () => void,
  onClearSelection?: () => void
) => {
  return (
    <Paper {...props}>
      {onToggleDeleted && (
        <Box
          component={MenuItem}
          role="menuitem"
          sx={{
            px: 2,
            py: 1,
            cursor: "pointer",
            fontWeight: 600,
            borderBottom: `1px solid ${COLORS.dividerColor}`,
            "&:hover": {
              backgroundColor: COLORS.activeBackground,
            },
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onClearSelection) {
              onClearSelection();
            }
            onToggleDeleted();
          }}
        >
          {isDeleted ? "Show Not Deleted" : "Show Deleted"}
        </Box>
      )}
      {props.children}
    </Paper>
  );
};

const AutoComplete: React.FC<IAutoCompleteProps> = ({
  options,
  value,
  labelComponent,
  label,
  onChange,
  multiple = false,
  placeholder = "Select an option",
  error = false,
  disabled = false,
  clearable = false,
  width = "100%",
  minWidth,
  sx = {},
  renderInput,
  getOptionLabel: _getOptionLabel,
  onInputChange,
  filterOptions = (
    opts: (string | DropdownOption)[],
    state: FilterOptionsState<string | DropdownOption>
  ) => {
    const inputValue = state.inputValue?.toLowerCase() || "";
    return opts.filter((option) => {
      const optLabel =
        typeof option === "string" || typeof option === "number"
          ? option === null || option === undefined
            ? ""
            : String(option)
          : option.label ?? "";
      return optLabel.toLowerCase().includes(inputValue);
    });
  },
  renderOption,
  freeSolo = false,
  highlightLabelStar = false,
  selectAll = false,
  selectFew,
  helperText = "",
  hideChips = false,
  disablePortal = false,
  isDeleted = false,
  onToggleDeleted,
  getOptionDisabled,
  onOpen,
}) => {
  const [open, setOpen] = useState(false);
  const useSelectFew = multiple && typeof selectFew === "number" && selectFew > 0;
  const useBulkSelect = multiple && (selectAll || useSelectFew);

  const getSelectableOptions = () =>
    getOptionDisabled
      ? options.filter((opt) => !getOptionDisabled(opt))
      : options;

  const getBulkSelectTarget = () => {
    const selectableOptions = getSelectableOptions();
    if (useSelectFew) {
      return selectableOptions.slice(0, selectFew);
    }
    return selectableOptions;
  };

  const isBulkTargetSelected = () => {
    const target = getBulkSelectTarget();
    if (!Array.isArray(value) || target.length === 0) return false;
    const selected = new Set(value.map(String));
    return target.every((opt) => selected.has(String(opt.value)));
  };

  const getOptionLabel = (option: string | DropdownOption): string => {
    // Handle SELECT_ALL_OPTION
    if (
      typeof option === "object" &&
      option !== null &&
      "value" in option &&
      option.value === "__SELECT_ALL__"
    ) {
      if (useSelectFew) {
        return isBulkTargetSelected()
          ? "Deselect All"
          : `Select First ${selectFew}`;
      }
      const selectableOptions = getSelectableOptions();
      const allSelected =
        multiple &&
        selectAll &&
        Array.isArray(value) &&
        selectableOptions.length > 0 &&
        value.length === selectableOptions.length;
      return allSelected ? "Deselect All" : "Select All";
    }

    if (typeof option === "string" || typeof option === "number") {
      const found = findOptionByValue(option, options);
      return found ? found.label : String(option);
    }
    return option.label ?? "";
  };
  let normalizedValue: any = null;

  if (multiple) {
    normalizedValue = Array.isArray(value)
      ? value
          .filter((v) => {
            // Filter out SELECT_ALL_OPTION from normalized value
            if (typeof v === "object" && v !== null && "value" in v) {
              return v.value !== "__SELECT_ALL__";
            }
            return v !== "__SELECT_ALL__";
          })
          .map((v) => {
            let val: string | number;
            if (typeof v === "object" && v !== null && "value" in v) {
              val = v.value;
            } else {
              val = v as string | number;
            }
            const found = findOptionByValue(val, options);
            return found || { label: String(val), value: val };
          })
      : [];
  } else {
    let val: string | number | null;
    if (typeof value === "object" && value !== null && "value" in value) {
      val = value.value as string | number | "";
    } else if (
      typeof value === "string" ||
      typeof value === "number" ||
      value === null
    ) {
      val = value;
    } else {
      val = "";
    }
    if (val === null || val === "") {
      normalizedValue = "";
    } else {
      const found = findOptionByValue(val, options);
      normalizedValue = found || { label: String(val), value: val };
    }
  }

  const CLEAR_OPTION = { label: "Select Any ", value: "" };
  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== null && value !== "";

  // Don't show CLEAR_OPTION when using bulk-select mode (checkbox multi-select)
  let displayOptions =
    clearable && hasValue && !useBulkSelect ? [CLEAR_OPTION, ...options] : options;

  // Add Select All / Select First N option at the beginning for multi-select bulk mode
  if (useBulkSelect) {
    displayOptions = [SELECT_ALL_OPTION, ...displayOptions];
  }

  interface ClearOption {
    label: string;
    value: string;
  }

  type OptionType = string | DropdownOption | ClearOption;

  interface EffectiveFilterOptionsState
    extends FilterOptionsState<OptionType> {}

  type EffectiveFilterOptions = (
    opts: OptionType[],
    state: EffectiveFilterOptionsState
  ) => OptionType[];

  const effectiveFilterOptions: EffectiveFilterOptions = (opts, state) => {
    // Separate SELECT_ALL_OPTION from other options
    const selectAllOpt = opts.find(
      (opt) =>
        typeof opt === "object" &&
        "value" in opt &&
        opt.value === "__SELECT_ALL__"
    );
    const otherOpts = opts.filter(
      (opt) =>
        !(
          typeof opt === "object" &&
          "value" in opt &&
          opt.value === "__SELECT_ALL__"
        )
    );

    if (state.inputValue && state.inputValue === CLEAR_OPTION.label) {
      return opts;
    }
    const filtered = (
      filterOptions ||
      ((options: OptionType[], state: EffectiveFilterOptionsState) => {
        const inputValue = state.inputValue?.toLowerCase() || "";
        return options.filter((option) => {
          const label =
            typeof option === "string" ? option : option.label ?? "";
          return label.toLowerCase().includes(inputValue);
        });
      })
    )(otherOpts, state);

    if (state.inputValue) {
      const filteredWithoutClear = filtered.filter(
        (opt) =>
          opt !== CLEAR_OPTION &&
          (typeof opt !== "object" || (opt as ClearOption).value !== "")
      );
      // Keep SELECT_ALL_OPTION at the top even when filtering
      return selectAllOpt
        ? [selectAllOpt, ...filteredWithoutClear]
        : filteredWithoutClear;
    }
    // Keep SELECT_ALL_OPTION at the top
    return selectAllOpt ? [selectAllOpt, ...filtered] : filtered;
  };

  interface CustomRenderOptionProps
    extends React.HTMLAttributes<HTMLLIElement> {}

  const customRenderOption = (
    props: CustomRenderOptionProps,
    option: string | DropdownOption | ClearOption
  ): React.ReactNode => {
    // Check for Select All / Select First N option
    if (
      option &&
      typeof option === "object" &&
      (option as DropdownOption).value === "__SELECT_ALL__"
    ) {
      const bulkSelected = isBulkTargetSelected();
      const bulkLabel = useSelectFew
        ? bulkSelected
          ? "Deselect All"
          : `Select First ${selectFew}`
        : bulkSelected
          ? "Deselect All"
          : "Select All";
      return (
        <li {...props} style={{ padding: "8px 16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <CustomCheckbox
              checked={bulkSelected}
              onChange={() => {}}
              ariaLabel={useSelectFew ? "select first few" : "select all"}
            />
            <Box component="span" sx={{ ml: 1, flexGrow: 1 }}>
              {bulkLabel}
            </Box>
          </Box>
        </li>
      );
    }

    if (
      option &&
      typeof option === "object" &&
      (option as ClearOption).value === "" &&
      (option as ClearOption).label === CLEAR_OPTION.label
    ) {
      return (
        <li {...props} style={{ color: COLORS.placeHolderColor }}>
          {CLEAR_OPTION.label}
        </li>
      );
    }

    // Render with checkbox if multiple and bulk-select mode
    if (useBulkSelect) {
      const optionValue =
        typeof option === "object" && "value" in option ? option.value : option;
      const isChecked =
        Array.isArray(normalizedValue) &&
        normalizedValue.some(
          (v: DropdownOption) => String(v.value) === String(optionValue)
        );
      const atLimit =
        useSelectFew &&
        Array.isArray(normalizedValue) &&
        normalizedValue.length >= selectFew! &&
        !isChecked;
      const isOptionDisabled =
        Boolean((props as any)["aria-disabled"]) || atLimit;
      const displayLabel = getOptionLabel(option as string | DropdownOption);
      return (
        <li
          {...props}
          style={{
            padding: "8px 16px",
            ...(isOptionDisabled
              ? { opacity: 0.5, pointerEvents: "none" as const }
              : {}),
          }}
          aria-disabled={isOptionDisabled || undefined}
        >
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <CustomCheckbox
              checked={isChecked}
              onChange={() => {}}
              ariaLabel={`Select ${displayLabel}`}
            />
            <Box component="span" sx={{ ml: 1, flexGrow: 1 }}>
              {displayLabel}
            </Box>
          </Box>
        </li>
      );
    }

    const displayLabel = getOptionLabel(option as string | DropdownOption);
    return <li {...props}>{displayLabel}</li>;
  };
  const handleChange = (
    event: React.SyntheticEvent,
    newValue: string | DropdownOption | (string | DropdownOption)[] | null,
    reason: any,
    details?: any
  ) => {
    if (multiple) {
      // Check if Select All / Select First N was clicked
      if (Array.isArray(newValue)) {
        const hasSelectAll = newValue.some(
          (v) =>
            typeof v === "object" &&
            "value" in v &&
            v.value === "__SELECT_ALL__"
        );

        if (hasSelectAll) {
          const target = getBulkSelectTarget();
          if (isBulkTargetSelected()) {
            onChange([]);
          } else {
            onChange(target.map((opt) => opt.value));
          }
          return;
        }
      }

      const values = (Array.isArray(newValue) ? newValue : [])
        .filter((v) => {
          // Filter out SELECT_ALL_OPTION and CLEAR_OPTION
          if (typeof v === "object" && "value" in v) {
            return v.value !== "__SELECT_ALL__" && v.value !== "";
          }
          return true;
        })
        .map((v) => {
          if (typeof v === "string" || typeof v === "number") {
            const matchedOption = findOptionByValue(v, options);
            return matchedOption ? matchedOption.value : v;
          }
          return v && typeof v === "object" && "value" in v ? v.value : v;
        });

      // Cap selection when selectFew is active
      if (useSelectFew && values.length > selectFew!) {
        onChange(values.slice(0, selectFew));
        return;
      }

      // For multiple select, always return an array (empty or with values)
      onChange(values);
    } else if (
      newValue &&
      typeof newValue === "object" &&
      "value" in newValue
    ) {
      onChange(newValue.value);
    } else if (typeof newValue === "string" || typeof newValue === "number") {
      const matchedOption = findOptionByValue(newValue, options);
      onChange(matchedOption ? matchedOption.value : newValue);
    } else {
      onChange("");
    }
  };

  // Custom renderTags for hideChips mode
  const renderTags =
    hideChips && multiple
      ? (value: any[], getTagProps: any) => {
          if (!value || value.length === 0) return null;

          // Check if all options are selected
          if (value.length === options.length) {
            return (
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                All Selected
              </Box>
            );
          }

          // Show comma-separated labels
          const labels = value
            .map((v) => {
              if (typeof v === "object" && "label" in v) {
                return v.label;
              }
              return "";
            })
            .filter((label) => label !== "") // Filter out empty labels
            .join(", ");

          // Don't render anything if labels is empty
          if (!labels) return null;

          return (
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {labels}
            </Box>
          );
        }
      : undefined;

  // Determine if we should show placeholder
  const hasSelectedValues = multiple
    ? Array.isArray(normalizedValue) && normalizedValue.length > 0
    : normalizedValue !== "" && normalizedValue !== null;

  const shouldShowPlaceholder =
    hideChips && hasSelectedValues ? "" : placeholder;

  const handleClearSelection = () => {
    if (multiple) {
      onChange([]);
    } else {
      onChange("");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {label && highlightLabelStar && (
        <CustomLabel
          text={label}
          fontSize="14px"
          fontWeight={500}
          color={COLORS.typographyColor}
          highlightLabelStar={highlightLabelStar}
        />
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Autocomplete
          multiple={multiple}
          options={displayOptions}
          getOptionLabel={getOptionLabel}
          freeSolo={freeSolo}
          clearIcon={clearable !== false ? undefined : null}
          clearOnEscape={!!clearable}
          renderTags={renderTags}
          disablePortal={disablePortal}
          open={open}
          onOpen={() => {
            setOpen(true);
            onOpen?.();
          }}
          onClose={() => setOpen(false)}
          getOptionDisabled={
            useBulkSelect || getOptionDisabled
              ? (option) => {
                  if (
                    typeof option === "object" &&
                    "value" in option &&
                    (option.value === "__SELECT_ALL__" || option.value === "")
                  ) {
                    return false;
                  }
                  if (getOptionDisabled?.(option as DropdownOption)) {
                    return true;
                  }
                  if (useSelectFew && Array.isArray(normalizedValue)) {
                    const optionValue =
                      typeof option === "object" && "value" in option
                        ? option.value
                        : option;
                    const isChecked = normalizedValue.some(
                      (v: DropdownOption) =>
                        String(v.value) === String(optionValue)
                    );
                    if (!isChecked && normalizedValue.length >= selectFew!) {
                      return true;
                    }
                  }
                  return false;
                }
              : undefined
          }
          renderInput={
            renderInput ||
            ((params: any) => (
              <CustomTextField
                {...params}
                label={highlightLabelStar ? "" : label}
                placeholder={shouldShowPlaceholder}
                error={error}
                sx={sx}
                minWidth={minWidth}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "off",
                }}
              />
            ))
          }
          value={normalizedValue}
          onChange={handleChange}
          disableCloseOnSelect={multiple}
          onInputChange={onInputChange}
          renderOption={customRenderOption}
          filterOptions={effectiveFilterOptions}
          PaperComponent={(props) =>
            CustomPaper(
              props,
              isDeleted,
              onToggleDeleted,
              handleClearSelection
            )
          }
          disabled={disabled}
          slotProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [
                {
                  name: "flip",
                  enabled: false,
                },
                {
                  name: "preventOverflow",
                  enabled: false,
                },
              ],
              style: {
                zIndex: 1300,
              },
            },
            paper: {
              style: {
                overflow: "hidden",
              },
            },
            listbox: {
              style: {
                maxHeight: "300px",
                overflow: "auto",
              },
            },
          }}
          sx={{
            ...sx,
            width: width,
            flex: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
            "& .MuiAutocomplete-tag": {
              backgroundColor: COLORS.activeBackground,
              color: "white",
              borderRadius: "12px",
              margin: "2px",
              padding: "2px 6px",
            },
          }}
        />
        {labelComponent}
      </Box>
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </Box>
  );
};

export default AutoComplete;
